import {
	BasicType,
	AdvancedType,
	DynamicType,
	LEGACY_RAW_BLOCK_TYPE,
} from '@bay-email/core';
import { IconType } from 'react-icons';
// Import Bootstrap Icons
import {
	BsFonts, // Text
	BsAt, // Footer (@)
	BsLayoutSplit, // Section
	BsColumns, // Column
	BsDash, // Divider
	BsImage, // Image
	BsCursor, // Button
	BsGrid3X3, // Table
	BsCollection, // Group
	BsFileText, // Page
	BsLayoutSidebarInset, // Wrapper
	BsList, // Navbar
	BsLayoutTextWindow, // Hero
	BsArrowsExpand, // Spacer
	BsShare, // Social
	BsImages, // Carousel
	BsChevronDown, // Accordion
	BsCodeSlash, // Raw/HTML - code brackets
	BsPersonVcard, // Signature - person vcard icon
	BsQuestionCircle, // Default fallback
	BsHouse, // Single house icon for single listing
	BsHouses, // Multiple houses icon for listings
	BsHouseGear, // House gear icon for criteria block
	BsPeople, // Multiple people icon for users
	BsPerson, // Single person icon for single user
	BsPersonGear, // Person gear icon for user criteria
	BsFileRichtext,
} from 'react-icons/bs';
import HomeBracketsIcon from '@bay-ui/Icons/HomeBracketsIcon';
import { IBlockData } from '@bay-email/core';

const FOOTER_BLOCK_CLASS = 'bay-email-footer-block';

function isFooterBlockData(blockData?: Partial<IBlockData> | null) {
	if (!blockData) return false;

	const isFooterFlag = Boolean((blockData as any)?.data?.value?.isFooterBlock);
	const cssClass = String((blockData as any)?.attributes?.['css-class'] || '');
	const content = String((blockData as any)?.data?.value?.content || '').toLowerCase();

	return (
		isFooterFlag ||
		cssClass.includes(FOOTER_BLOCK_CLASS) ||
		(content.includes('equal housing opportunity') &&
			content.includes('unsubscribe'))
	);
}

// Define the custom block type locally to avoid circular dependencies
const CustomBlockType = {
	CRITERIA_BLOCK: 'criteria_block' as const,
	LISTINGS_BLOCK: 'listings_block' as const,
	SINGLE_LISTING_BLOCK: 'text_single_listing' as const,
	CUSTOM_LISTING_BLOCK: 'custom_listing_block' as const,
	USERS_CRITERIA_BLOCK: 'users_criteria_block' as const,
	USERS_BLOCK: 'users_block' as const,
	SINGLE_USER_BLOCK: 'text_single_user' as const,
	SIGNATURE_BLOCK: 'signature_block' as const,
};

// Icon mapping with Bootstrap Icons
let iconsMap: Record<string, IconType> = {
	// Basic types - all Bootstrap Icons now
	[BasicType.TEXT]: BsFonts,
	[BasicType.SECTION]: BsLayoutSplit,
	[BasicType.COLUMN]: BsColumns,
	[BasicType.DIVIDER]: BsDash,
	[BasicType.IMAGE]: BsImage,
	[BasicType.BUTTON]: BsCursor,
	[BasicType.GROUP]: BsCollection,
	[BasicType.TABLE]: BsGrid3X3,
	[BasicType.PAGE]: BsFileText,
	[BasicType.WRAPPER]: BsLayoutSidebarInset,
	[BasicType.NAVBAR]: BsList,
	[BasicType.HERO]: BsLayoutTextWindow,
	[BasicType.SPACER]: BsArrowsExpand,
	[BasicType.SOCIAL]: BsShare,
	[BasicType.CAROUSEL]: BsImages,
	[BasicType.ACCORDION]: BsChevronDown,
	[BasicType.RAW]: BsCodeSlash,
	[LEGACY_RAW_BLOCK_TYPE]: BsCodeSlash,

	// Advanced types (same as basic)
	[AdvancedType.TEXT]: BsFonts,
	[AdvancedType.DIVIDER]: BsDash,
	[AdvancedType.IMAGE]: BsImage,
	[AdvancedType.BUTTON]: BsCursor,
	[AdvancedType.NAVBAR]: BsList,
	[AdvancedType.SPACER]: BsArrowsExpand,
	[AdvancedType.SOCIAL]: BsShare,
	[AdvancedType.CAROUSEL]: BsImages,
	[AdvancedType.ACCORDION]: BsChevronDown,
	[AdvancedType.WRAPPER]: BsLayoutSidebarInset,
	[AdvancedType.SECTION]: BsLayoutSplit,
	[AdvancedType.COLUMN]: BsColumns,
	[AdvancedType.GROUP]: BsCollection,
	[AdvancedType.TABLE]: BsGrid3X3,
	[AdvancedType.HERO]: BsLayoutTextWindow,

	// Dynamic types
	[DynamicType.IMAGE]: BsFileRichtext,

	// Custom blocks
	[CustomBlockType.CRITERIA_BLOCK]: BsHouseGear, // House gear icon for criteria block
	[CustomBlockType.LISTINGS_BLOCK]: BsHouses, // Multiple houses icon for listings
	[CustomBlockType.SINGLE_LISTING_BLOCK]: BsHouse, // House icon for single listing
	[CustomBlockType.CUSTOM_LISTING_BLOCK]: HomeBracketsIcon,
	[CustomBlockType.USERS_CRITERIA_BLOCK]: BsPersonGear, // Person gear icon for user criteria block
	[CustomBlockType.USERS_BLOCK]: BsPeople, // Multiple people icon for users
	[CustomBlockType.SINGLE_USER_BLOCK]: BsPerson, // Single person icon for single user
	[CustomBlockType.SIGNATURE_BLOCK]: BsPersonVcard, // Person vcard icon for signature block
};

/**
 * Get React Icon component by block type
 * @param type Block type string
 * @returns React Icon component
 */
export function getIconByBlockType(
	type: string,
	blockData?: Partial<IBlockData> | null,
): IconType {
	if (
		(type === BasicType.TEXT || type === AdvancedType.TEXT) &&
		isFooterBlockData(blockData)
	) {
		return BsAt;
	}

	return iconsMap[type] || BsQuestionCircle; // Default fallback
}

/**
 * Set custom icon mappings
 * @param map Custom icon mappings
 */
export function setIconsMap(map: Record<string, IconType>) {
	iconsMap = { ...iconsMap, ...map };
}

// Backward compatibility - return icon name for old system
export function getIconNameByBlockType(type: string): string {
	// This is for backward compatibility only
	// Eventually we should remove this and update all usages to use getIconByBlockType
	const iconMap: Record<string, string> = {
		[BasicType.TEXT]: 'icon-text',
		[BasicType.SECTION]: 'icon-section',
		[BasicType.COLUMN]: 'icon-column',
		[BasicType.DIVIDER]: 'icon-divider',
		[BasicType.IMAGE]: 'icon-img',
		[BasicType.BUTTON]: 'icon-button',
		[BasicType.GROUP]: 'icon-group',
		[BasicType.PAGE]: 'icon-page',
		[BasicType.WRAPPER]: 'icon-wrapper',
		[BasicType.NAVBAR]: 'icon-navbar',
		[BasicType.HERO]: 'icon-hero',
		[BasicType.SPACER]: 'icon-spacing',
		[BasicType.SOCIAL]: 'icon-social',
		[BasicType.CAROUSEL]: 'icon-carousel',
		[BasicType.ACCORDION]: 'icon-accordion',
		// Advanced types
		[AdvancedType.TEXT]: 'icon-text',
		[AdvancedType.DIVIDER]: 'icon-divider',
		[AdvancedType.IMAGE]: 'icon-img',
		[AdvancedType.BUTTON]: 'icon-button',
		[AdvancedType.NAVBAR]: 'icon-navbar',
		[AdvancedType.SPACER]: 'icon-spacing',
		[AdvancedType.SOCIAL]: 'icon-social',
		[AdvancedType.CAROUSEL]: 'icon-carousel',
		[AdvancedType.ACCORDION]: 'icon-accordion',
		[AdvancedType.WRAPPER]: 'icon-wrapper',
		[AdvancedType.SECTION]: 'icon-section',
		[AdvancedType.COLUMN]: 'icon-column',
		[AdvancedType.GROUP]: 'icon-group',
		[AdvancedType.HERO]: 'icon-hero',
		// Dynamic types
		[DynamicType.IMAGE]: 'file-rich-text',
		// Custom blocks
		[CustomBlockType.CRITERIA_BLOCK]: 'icon-house-gear',
		[CustomBlockType.LISTINGS_BLOCK]: 'icon-houses',
		[CustomBlockType.SINGLE_LISTING_BLOCK]: 'icon-house',
		[CustomBlockType.USERS_BLOCK]: 'icon-people',
		[CustomBlockType.SINGLE_USER_BLOCK]: 'icon-person',
	};

	return iconMap[type] || 'icon-number';
}
